import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, MessageCircle, X } from 'lucide-react';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';

const MENU_TITLE_FONT = "'Great Vibes', cursive";
const SECTION_FONT = "'Cormorant Garamond', 'Playfair Display', serif";

const leftColumn = [
  {
    type: 'package',
    header: 'OPTION 1 – R180,00 PP',
    cartId: 'pkg-opt1',
    price: 180,
    priceLabel: 'R180,00 PP',
    bullets: ['2 Meat', '2 Starch', '2 Veggies', '2 Salads'],
  },
  {
    type: 'package',
    header: 'OPTION 3 – R245,00 PP',
    cartId: 'pkg-opt3',
    price: 245,
    priceLabel: 'R245,00 PP',
    bullets: ['Starters', '2 Meat', '2 Starch', '2 Veggies', '2 Salads', 'Dessert/Soft Drink'],
  },
  {
    type: 'priced-list',
    header: 'PLATTERS, CAN SERVE 10–12 PAX',
    items: [
      { cartId: 'plt-savoury', name: 'Savoury Platter', price: 550, priceLabel: 'R550,00' },
      { cartId: 'plt-veg', name: 'Vegetarian Platter', price: 450, priceLabel: 'R450,00' },
      { cartId: 'plt-meat', name: 'Meat Platter', price: 550, priceLabel: 'R550,00' },
      { cartId: 'plt-fruit', name: 'Fruit Platter', price: 400, priceLabel: 'R400,00' },
      { cartId: 'plt-hightea', name: 'High Tea Platter', price: 400, priceLabel: 'R400,00' },
      { cartId: 'plt-sandwich', name: 'Sandwich Platter', price: 400, priceLabel: 'R400,00' },
      { cartId: 'plt-cocktail', name: 'Cocktail Platter', price: 550, priceLabel: 'R550,00' },
      { cartId: 'plt-sweet', name: 'Sweet Platter', price: 400, priceLabel: 'R400,00' },
    ],
  },
  {
    type: 'priced-list',
    header: 'OTHER OPTIONS',
    items: [
      { cartId: 'oth-english', name: 'English Breakfast', price: 100, priceLabel: 'R100,00' },
      { cartId: 'oth-full', name: 'Full Breakfast', price: 150, priceLabel: 'R150,00' },
      { cartId: 'oth-boerie', name: 'Boerie & Chips', price: 70, priceLabel: 'R70,00' },
      {
        cartId: 'oth-snack',
        name: 'Snack Basket (Chips, Nuggets, Samoosa & Cheese Griller)',
        price: 80,
        priceLabel: 'R80,00',
      },
      { cartId: 'oth-burger', name: 'Burger & Chips', price: 90, priceLabel: 'R90,00' },
      { cartId: 'oth-wrap', name: 'Wrap & Chips', price: 70, priceLabel: 'R70,00' },
    ],
  },
];

const rightColumn = [
  {
    type: 'package',
    header: 'OPTION 2 – R210,00 PP',
    cartId: 'pkg-opt2',
    price: 210,
    priceLabel: 'R210,00 PP',
    bullets: ['2 Meat', '2 Starch', '2 Veggies', '2 Salads', 'Dessert/Soft Drink'],
  },
  {
    type: 'package',
    header: 'UPSIZED – R285,00 PP',
    cartId: 'pkg-upsized',
    price: 285,
    priceLabel: 'R285,00 PP',
    bullets: ['3 Meat', '3 Starch', '3 Veggies', '3 Salads', 'Dessert/Soft Drink'],
  },
  {
    type: 'package',
    header: 'BRAAI – R250,00 PP',
    cartId: 'pkg-braai',
    price: 250,
    priceLabel: 'R250,00 PP',
    bullets: ['3 Meat', '2 Starch', '2 Veggies', '2 Salads', 'Dessert/Soft Drink'],
  },
  {
    type: 'dessert-list',
    header: 'DESSERT R30 – R40,00',
    items: [
      { cartId: 'des-brownie', name: 'Brownie & Ice Cream', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-milk', name: 'Milk Tart', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-peppermint', name: 'Peppermint Tart', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-choc', name: 'Chocolate Cake & Custard', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-malva', name: 'Malva & Custard', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-fruit', name: 'Mixed Fruits & Custard', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-jelly', name: 'Jelly & Custard', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-ice', name: 'Ice Cream', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-waffle', name: 'Waffle', price: 35, priceLabel: 'R30 – R40,00' },
      { cartId: 'des-cake', name: 'Cake', price: 35, priceLabel: 'R30 – R40,00' },
    ],
  },
];

const priceIncludes = [
  'Groceries',
  'Cutlery & Crockery',
  'Serving Tables with Clothes',
  'Serving Dishes',
  'Preparation, Cook, Serve & Clean-Up',
];

const faqItems = [
  { q: 'Do you provide catering in Pretoria and Gauteng?', a: 'Yes, we cater in Pretoria, Centurion, Midrand, Johannesburg, and nearby Gauteng areas.' },
  { q: 'Can I customize my menu?', a: 'Yes. You can choose packages, platter options, and add-ons for your event requirements.' },
  { q: 'How do I get a quote fast?', a: 'Use the menu cart and send your selection to WhatsApp for quick assistance.' },
  { q: 'Do you offer corporate catering near me?', a: 'Yes. We support office and corporate event catering throughout Gauteng.' },
  { q: 'How early should I book?', a: 'Weekend bookings fill fast, so we recommend early booking to secure your date.' },
];

const Menu = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    const savedItems = localStorage.getItem('ubuntu-events-management-catering-items');
    if (savedItems) {
      setSelectedItems(JSON.parse(savedItems));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('ubuntu-events-management-catering-items', JSON.stringify(selectedItems));
  }, [selectedItems]);

  const cartItemFromPayload = (payload) => ({
    id: payload.cartId,
    name: payload.name,
    price: payload.price,
    priceLabel: payload.priceLabel,
  });

  const addToCart = (payload) => {
    const item = cartItemFromPayload(payload);
    const existingItem = selectedItems.find((s) => s.id === item.id);
    if (existingItem) {
      setSelectedItems(
        selectedItems.map((s) =>
          s.id === item.id ? { ...s, quantity: s.quantity + 1 } : s
        )
      );
    } else {
      setSelectedItems([...selectedItems, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      setSelectedItems(
        selectedItems.map((item) =>
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const getQuantity = (cartId) =>
    selectedItems.find((s) => s.id === cartId)?.quantity ?? 0;

  const bumpQuantity = (payload) => {
    const id = payload.cartId;
    const q = getQuantity(id);
    if (q === 0) addToCart(payload);
    else updateQuantity(id, q + 1);
  };

  const MenuQuantityControl = ({ payload }) => {
    const id = payload.cartId;
    const q = getQuantity(id);
    const btn =
      'flex h-8 w-8 items-center justify-center rounded text-sm font-semibold text-black transition-colors disabled:opacity-35 disabled:pointer-events-none hover:bg-black/10';
    return (
      <div
        className="inline-flex items-center rounded-md border border-black/25 bg-white/60 shadow-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={btn}
          aria-label="Decrease quantity"
          disabled={q === 0}
          onClick={() => updateQuantity(id, q - 1)}
        >
          −
        </button>
        <span
          className="min-w-[2rem] px-1 text-center text-sm font-semibold tabular-nums text-black"
          aria-live="polite"
        >
          {q}
        </span>
        <button
          type="button"
          className={btn}
          aria-label="Increase quantity"
          onClick={() => bumpQuantity(payload)}
        >
          +
        </button>
      </div>
    );
  };

  const getTotalPrice = () =>
    selectedItems.reduce((total, item) => total + item.price * item.quantity, 0);

  const buildWhatsAppListMessage = () => {
    const lines = selectedItems.map((item, index) => {
      const unit = item.priceLabel || `R${item.price}`;
      const lineTotal = item.price * item.quantity;
      return `${index + 1}. ${item.name}\n   ${item.quantity} × ${unit} = R${lineTotal}`;
    });
    return `Hi UBUNTU EVENTS MANAGEMENT,

I'd like a quote for the following selections:

${lines.join('\n\n')}

────────────
Total: R${getTotalPrice()}
────────────

Please confirm availability and let me know the next steps.`;
  };

  const sendWhatsAppMessage = () => {
    if (selectedItems.length === 0) return;
    const message = buildWhatsAppListMessage();
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/27663286231?text=${encodedMessage}`;
    if (url.length > 8000) {
      window.alert(
        'Your selection list is very long for a single WhatsApp link. Please remove a few items in Review, or send two separate messages.'
      );
      setShowCart(true);
      return;
    }
    window.open(url, '_blank');
  };

  const SectionHeader = ({ children }) => (
    <h3
      className="text-[0.95rem] sm:text-base tracking-[0.12em] font-semibold text-black uppercase mb-3"
      style={{ fontFamily: SECTION_FONT }}
    >
      {children}
    </h3>
  );

  const PackageBlock = ({ section }) => {
    const payload = {
      cartId: section.cartId,
      name: section.header,
      price: section.price,
      priceLabel: section.priceLabel,
    };
    return (
      <div>
        <SectionHeader>{section.header}</SectionHeader>
        <ul className="space-y-1 text-sm sm:text-[0.9375rem] text-black font-semibold leading-snug">
          {section.bullets.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-black/55">Quantity</span>
          <MenuQuantityControl payload={payload} />
        </div>
      </div>
    );
  };

  const PricedListBlock = ({ section, dessert }) => (
    <div>
      <SectionHeader>{section.header}</SectionHeader>
      <ul className="space-y-3 text-sm sm:text-[0.9375rem] text-black leading-snug">
        {section.items.map((row) => (
          <li
            key={row.cartId}
            className="flex flex-col gap-2 border-b border-black/[0.08] pb-3 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:gap-3"
          >
            <div className="min-w-0 flex-1">
              <span className="font-semibold block">{row.name}</span>
              {!dessert && (
                <span className="mt-0.5 block text-xs font-semibold text-black/70">{row.priceLabel}</span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
              <MenuQuantityControl payload={row} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderColumn = (sections) =>
    sections.map((section) => {
      if (section.type === 'package') {
        return <PackageBlock key={section.cartId} section={section} />;
      }
      if (section.type === 'priced-list') {
        return <PricedListBlock key={section.header} section={section} dessert={false} />;
      }
      if (section.type === 'dessert-list') {
        return <PricedListBlock key={section.header} section={section} dessert />;
      }
      return null;
    });

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 bg-[#ebe6dc] ${selectedItems.length > 0 ? 'pb-32 sm:pb-28' : ''}`}
    >
      <SeoHead
        title="Catering Services Pretoria | Ubuntu Enterprises"
        description="Catering services in Pretoria and Gauteng with buffet, platters, and event menu options."
        canonicalPath="/menu"
        faqSchema={faqItems}
      />
      <div
        className="max-w-4xl mx-auto relative rounded-sm px-5 py-10 sm:px-10 sm:py-12 shadow-md border border-black/[0.06]"
        style={{
          backgroundColor: '#f5f0e6',
          backgroundImage: `
            linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 45%),
            url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.045'/%3E%3C/svg%3E")
          `,
        }}
      >
        <header className="text-center mb-10 sm:mb-12">
          <h1
            className="text-5xl sm:text-6xl md:text-7xl font-normal text-black leading-none mb-4"
            style={{ fontFamily: MENU_TITLE_FONT }}
          >
            Menu
          </h1>
          <p className="text-sm sm:text-base font-bold text-black tracking-tight max-w-xl mx-auto px-2">
            Our Buffet Price Starts From R180 - R285,00 Per Person
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-0">
          <div className="md:pr-8 md:border-r md:border-black font-['Poppins',sans-serif] text-left space-y-10">
            {renderColumn(leftColumn)}
          </div>
          <div className="md:pl-8 font-['Poppins',sans-serif] text-left space-y-10 border-t border-black pt-10 md:border-t-0 md:pt-0">
            {renderColumn(rightColumn)}
          </div>
        </div>

        <footer className="mt-12 sm:mt-14 pt-8 border-t border-black/20 text-center">
          <p className="text-sm font-semibold text-black mb-3">Price Includes:</p>
          <ul className="text-xs sm:text-sm text-black/90 space-y-1">
            {priceIncludes.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </footer>
      </div>

      {selectedItems.length > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/15 bg-[#f5f0e6]/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="flex items-center gap-3 text-left text-black min-w-0 flex-1"
              aria-label="Review selections"
            >
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[#f5f0e6]">
                <ShoppingCart size={20} />
                <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                  {selectedItems.length}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-black">
                  {selectedItems.length} selection{selectedItems.length !== 1 ? 's' : ''} · R{getTotalPrice()}
                </span>
                <span className="block text-xs text-black/55 truncate">
                  Tap to review, edit quantities, or send the full list to WhatsApp
                </span>
              </span>
            </button>
            <div className="flex gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowCart(true)}
                className="flex-1 sm:flex-none rounded-lg border border-black/20 bg-white/60 px-4 py-2.5 text-sm font-semibold text-black hover:bg-white/90"
              >
                Review
              </button>
              <button
                type="button"
                onClick={sendWhatsAppMessage}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {showCart && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#f5f0e6] rounded-t-2xl sm:rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-black/10 shadow-xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-black" style={{ fontFamily: SECTION_FONT }}>
                Your booking
              </h3>
              <button
                type="button"
                onClick={() => setShowCart(false)}
                className="text-black/50 hover:text-black"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {selectedItems.map((item) => (
                <div key={item.id} className="flex justify-between items-center border-b border-black/10 pb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-black">{item.name}</h4>
                    <p className="text-xs text-black/60">
                      {item.priceLabel || `R${item.price}`} each
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center text-sm text-black"
                    >
                      −
                    </button>
                    <span className="w-7 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center text-sm text-black"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-semibold text-sm">R{item.price * item.quantity}</span>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-black/55 mb-3">
              The numbered list below is sent exactly as shown to WhatsApp (one line per selection).
            </p>
            <div className="rounded-lg border border-black/10 bg-white/40 p-3 mb-4 max-h-40 overflow-y-auto text-xs text-black/80 whitespace-pre-wrap font-mono leading-relaxed">
              {buildWhatsAppListMessage()}
            </div>

            <div className="border-t border-black/15 pt-4 mb-4">
              <div className="flex justify-between text-lg font-bold text-black">
                <span>Total:</span>
                <span>R{getTotalPrice()}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={sendWhatsAppMessage}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2"
            >
              <MessageCircle size={20} />
              <span>Send full list to WhatsApp</span>
            </button>
          </motion.div>
        </div>
      )}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto mt-10 rounded-xl border border-gold/20 bg-white p-8"
      >
        <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-3">
          Our Other Services
        </h3>
        <p className="text-gray-600 mb-6">
          Pair your catering package with decor styling, sound equipment, and quick navigation back to the homepage.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'decor' }))}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5"
          >
            Elegant Event Decor Services
          </button>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'soundhire' }))}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5"
          >
            Professional Sound Equipment Hire
          </button>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'home' }))}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5 sm:col-span-2"
          >
            Visit the Homepage
          </button>
        </div>
      </motion.section>
      <FAQSection title="Catering FAQs" faqs={faqItems} />
    </div>
  );
};

export default Menu;
